import {
    Box,
    Button,
    Grid,
    Link,
    MenuItem,
    Paper,
    Select,
    Stack,
    TextField,
    Tooltip,
    Typography,
    useTheme,
} from "@mui/material";
import { DataGridPro, GridToolbar } from "@mui/x-data-grid-pro";
import { useEffect, useRef, useState } from "react";
import { ProficiencyErrors } from "../apiClients/ProficiencyClient";
import ProficiencyGroupClient, { ProficiencyGroup } from "../apiClients/ProficiencyGroupClient";
import TalentClient, { TalentErrors } from "../apiClients/TalentClient";
import BusySpinner from "../components/BusySpinner";
import ConditionalDisplay from "../components/ConditionalDisplay";
import { useErrorDisplay } from "../components/ErrorDisplay";
import { IconButtonExProps } from "../components/IconButtonEx";
import PanelSegment from "../components/PanelSegment";
import { ExtractForSetError, IfTrue, IsNotEmpty, IsNullOrUndefined } from "../Helpers";
import { I18N } from "../I18N";
import { AbilityLevel, BehaviorLevel, OngoingAssessment, ProficiencyGraph, UpdateProficiencySelection } from "../models/dtos";
import OngoingAssessmentPublishPopup from "../popups/OngoingAssessmentPublishPopup";
import PublishIcon from '@mui/icons-material/Publish';
import AutoAwesomeRounded from '@mui/icons-material/AutoAwesomeRounded';
import { useWindowDimensions } from "../components/WindowDimensions";
import TextEditorPopup from "../components/TextEditorPopup";
import { OngoingAssessmentErrors } from "../apiClients/OngoingAssessmentClient";
import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";
import PrintIcon from '@mui/icons-material/Print';
import dayjs from "dayjs";
import AiReviewPanel from "./AiReviewPanel";
import ContextualAiDialog from "../components/ai/ContextualAiDialog";
import { MobileEntityCard } from "../components/responsive";
import useViewportMode from "../hooks/useViewportMode";

export interface OngoingAssessmentComponentProps extends Omit<IconButtonExProps, 'onClick'> {
    talentId: string;
    subscriptionActive: boolean;
    onPublish?: () => Promise<void>;
    heightDetractor?: number;
    assessmentLayoutOption?: boolean;
    showFooter?: boolean;
    /** Fill a height-constrained parent instead of deriving a viewport height. */
    fillAvailableHeight?: boolean;
    onAiDialogOpenChange?: (open: boolean) => void;
}

const OngoingAssessmentComponent = ({ talentId, subscriptionActive, heightDetractor = 300, assessmentLayoutOption = false, showFooter = true, fillAvailableHeight = false, onPublish = async () => { }, onAiDialogOpenChange = (_open: boolean) => { } }: OngoingAssessmentComponentProps) => {

    const [ErrorDisplay, setError] = useErrorDisplay();
    const [busy, setBusy] = useState<boolean>(false);
    const assessmentGeneration = useRef<number>(0);
    const theme = useTheme();

    const [assessment, setAssessment] = useState<OngoingAssessment>(null);
    const [proficiencyGroups, setProficiencyGroups] = useState<ProficiencyGroup[]>([]);
    const [selectedProficiencyGroup, setSelectedProficiencyGroup] = useState<ProficiencyGroup>();
    const [noTemplateExists, setNoTemplateExists] = useState<boolean>(false);
    const [aiWorkspaceRevision, setAiWorkspaceRevision] = useState<number>(0);
    const [aiDialogOpen, setAiDialogOpen] = useState(false);
    const assessmentAnchorRef = useRef<HTMLDivElement | null>(null);

    const [abilityLevel, setAbilityLevels] = useState([]);
    const [behaviorLevel, setBehaviorLevels] = useState([]);

    const { height } = useWindowDimensions();
    const { mode: viewportMode, isDesktop } = useViewportMode();
    const aiWorkspaceEnabled = !assessmentLayoutOption && !IsNullOrUndefined(assessment);
    const useTouchAssessmentCards = !isDesktop;

    useEffect(() => {
        onAiDialogOpenChange(aiDialogOpen);
        return () => onAiDialogOpenChange(false);
    }, [aiDialogOpen, onAiDialogOpenChange]);

    useEffect(() => {
        const generation = ++assessmentGeneration.current;
        setAssessment(null);
        setProficiencyGroups([]);
        setSelectedProficiencyGroup(undefined);
        setNoTemplateExists(false);
        setBusy(false);
        setAiDialogOpen(false);

        if (subscriptionActive)
            void loadData(false, generation);

        return () => {
            if (generation === assessmentGeneration.current)
                assessmentGeneration.current += 1;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [talentId, subscriptionActive]);

    const loadData = async (resetSelection: boolean = false, generation: number = assessmentGeneration.current) => {
        try {
            setBusy(true);

            setAbilityDropdown();
            setBehaviorDropDown();

            // Get the Ongoing Assessment Graph for the Talent
            let oa = await GetOngoingAssessment(generation);
            if (generation !== assessmentGeneration.current)
                return;
            if (IsNullOrUndefined(oa)) {
                setNoTemplateExists(true);
            } else {
                setAssessment(oa);

                // List of Proficiency Groups / Criteria Categories
                let pg = await new ProficiencyGroupClient().GetAll();
                if (generation !== assessmentGeneration.current)
                    return;
                setProficiencyGroups(pg);

                if (pg.length > 0)
                    setSelectedProficiencyGroup(pg[0]);

            }

        } catch (error) {
            if (generation === assessmentGeneration.current)
                setError(error, TalentErrors.GetSingle);
        }
        finally {
            if (generation === assessmentGeneration.current)
                setBusy(false);
        }
    };

    const GetOngoingAssessment = async (generation: number = assessmentGeneration.current) => {

        const apiResponse = await new TalentClient().GetOngoingAssessment(talentId);
        if (generation !== assessmentGeneration.current)
            return;

        if (ExtractForSetError(apiResponse, TalentErrors.GetSingle, (message, subtitle, isValidation) => {
            if (generation === assessmentGeneration.current)
                setError(message, subtitle, isValidation);
        }))
            return;

        return apiResponse.Content;
    }

    const handleSelectionChanged = async (graph: ProficiencyGraph, formSelection: string) => {
        try {
            let updateDto: UpdateProficiencySelection = {
                ProficiencyId: graph.ProficiencyId,
                ProficiencySelectionId: graph.ProficiencySelectionId,
                Selection: formSelection,
                Notes: graph.Notes
            }

            await new TalentClient().UpdateProficiencySelection(talentId, updateDto);
            await refreshAssessment(false);

        } catch (error) {
            setError(error, ProficiencyErrors.Update);
        }
    };

    const handleNoteTextChanged = async (graph: ProficiencyGraph, note: string) => {
        try {
            let updateDto: UpdateProficiencySelection = {
                ProficiencyId: graph.ProficiencyId,
                ProficiencySelectionId: graph.ProficiencySelectionId,
                Selection: graph.Selection.SelectionText,
                Notes: note
            }

            await new TalentClient().UpdateProficiencySelection(talentId, updateDto);
            await refreshAssessment(false);

        } catch (error) {
            setError(error, ProficiencyErrors.Update);
        }

    }

    const handlePublishError = (message, subtitle?: string, isValidation?: boolean) => {
        isValidation = isValidation ?? false;
        subtitle = subtitle ?? OngoingAssessmentErrors.Publish;

        setError(message, subtitle, isValidation);
    }

    const refreshAssessment = async (showBusy: boolean = true) => {
        const generation = assessmentGeneration.current;
        try {
            if (showBusy) {
                setBusy(true);
            }

            let oa = await GetOngoingAssessment(generation);
            if (generation !== assessmentGeneration.current)
                return;
            setAssessment(oa);

        } catch (error) {
            if (generation === assessmentGeneration.current)
                setError(error, TalentErrors.UpdateProficiencySelection);
        }
        finally {
            if (showBusy && generation === assessmentGeneration.current) {
                setBusy(false);
            }
        }
    }

    const handleAssessmentPublished = async () => {
        setAiWorkspaceRevision(current => current + 1);
        await refreshAssessment(false);
        await onPublish();
    };

    const setAbilityDropdown = () => {

        let abilityLevels: string[] = [];
        for (var enumMember in AbilityLevel) {
            var isValueProperty = parseInt(enumMember, 10) >= 0;
            if (isValueProperty) {
                abilityLevels.push(AbilityLevel[enumMember]);
            }
        }
        setAbilityLevels(abilityLevels);
    }

    const setBehaviorDropDown = () => {

        let behaviorLevels: string[] = [];
        for (var enumMember in BehaviorLevel) {
            var isValueProperty = parseInt(enumMember, 10) >= 0;
            if (isValueProperty) {
                behaviorLevels.push(BehaviorLevel[enumMember]);
            }
        }
        setBehaviorLevels(behaviorLevels);
    }

    const getAlternateDisplayGridContainerStyle = () => {
        return { width: '100%', marginTop: theme.spacing(0) };
    }


    const groupBy = (array, key) => {
        return array.reduce((result, currentValue) => {
            (result[currentValue[key]] = result[currentValue[key]] || []).push(currentValue);
            return result;
        }, {});
    };

    const printDocument = (assessment) => {
        const doc = new jsPDF();
        const groupedProficiencies = groupBy(assessment.Proficiencies, 'ProficiencyGroupName');

        let startY = 10; // Initial vertical position for the first table
        let groupCounter = 0; // Counter for groups

        Object.keys(groupedProficiencies).forEach(group => {
            const tableColumn = ["Proficiency", "Type", "Selection"]; // define your columns here
            const tableRows = [];

            // Extract data from each proficiency and push into tableRows
            groupedProficiencies[group].forEach(proficiency => {
                tableRows.push([proficiency.Name, proficiency.Type, proficiency.Selection.SelectionText]);
            });

            // If two groups have been printed on the current page, add a new page
            if (groupCounter === 2) {
                doc.addPage();
                startY = 10; // Reset startY for the new page
                groupCounter = 0; // Reset group counter
            }

            // Draw a colored rectangle for the report header
            // doc.setFillColor(168, 199, 240); // Set the fill color 
            // doc.rect(10, startY, doc.internal.pageSize.width - 20, 30, 'F'); // Increase the height of the rectangle

            // Add report header
            if (groupCounter === 0) {
                doc.setFontSize(14);
                doc.text(`Talent Name: ${assessment.Talent.FirstName} ${assessment.Talent.LastName}`, 15, startY + 9); // Adjust the y-coordinate to position the text inside the rectangle
                doc.text(`Overall Score: ${assessment.ScoreCard.AdjustedScore}`, 15, startY + 19); // Adjust the y-coordinate to position the text inside the rectangle
                const dateString = dayjs().format('MM/DD/YYYY');
                // Add the date to the report header
                doc.setFontSize(12); // Set the font size to 12 for the date
                doc.text(`Report Date: ${dateString}`, 15, startY + 29); // Adjust the y-coordinate to position the date under the overall score
                startY += 45; // Adjust startY to leave space for the header
            }

            doc.setFontSize(12);

            const groupScore = assessment?.ScoreCard.ProficiencyGroupScores.find(score => score.ProficiencyGroup === group)?.Score;
            const formattedGroupScore = groupScore !== undefined ? groupScore.toFixed(1) : '0.0';

            doc.text(`Proficiency Group: ${group} - ${formattedGroupScore}`, 10, startY);
            autoTable(doc, {
                head: [tableColumn],
                body: tableRows,
                startY: startY + 2,
                margin: { left: 10 },
                willDrawCell: (data) => {
                    // Only stripe body rows, not the header row
                    if (data.section === 'body') {
                        // Get the row number
                        const rowNum = data.row.index;

                        // If the row number is even, fill the cell with a light gray color
                        if (rowNum % 2 === 0) {
                            doc.setFillColor(230, 230, 230);
                            doc.rect(data.cell.x, data.cell.y, data.cell.width, data.cell.height, 'F');
                        }
                    }
                }
            });

            // Update startY for the next table
            startY = (doc as any).lastAutoTable.finalY + 20; // 20 is the gap between tables

            groupCounter++; // Increment group counter
        });

        doc.save("assessment.pdf");
    };

    if (noTemplateExists)
        return (
            <>
                <Grid container spacing={1} sx={getAlternateDisplayGridContainerStyle}>
                    <Grid item xs={3}></Grid>
                    <Grid item xs={9}>
                        <Typography sx={{ fontSize: 'small', marginTop: theme.spacing(2), marginBottom: theme.spacing(2) }}>{`There is no active Template for this Talent's Position.`}</Typography>
                    </Grid>
                </Grid>

            </>
        )
    else if (!subscriptionActive)
        return (
            <>
                <Grid container spacing={1} sx={getAlternateDisplayGridContainerStyle}>
                    <Grid item xs={3}></Grid>
                    <Grid item xs={9}>
                        <Typography sx={{ fontSize: 'small', marginTop: theme.spacing(2), marginBottom: theme.spacing(2) }}>
                            {`You need an active subscription for Ongoing Assessments, `}
                            <Link href="/SubscriptionPage" target="_self" rel="noopener" underline="hover">{`subscribe here`}</Link>
                        </Typography>

                    </Grid>
                </Grid>
            </>
        )
    else
        return (
            <>
                <ErrorDisplay />

                <Box
                    data-testid={assessmentLayoutOption ? 'assessment-read-only-layout' : 'ai-assisted-review-workspace'}
                    data-workspace-layout="full-width"
                    sx={{
                        display: 'block',
                        alignItems: 'stretch',
                        height: aiWorkspaceEnabled
                            ? fillAvailableHeight
                                ? '100%'
                                : isDesktop
                                    ? Math.max(height - heightDetractor + 190, 640)
                                    : 'calc(100dvh - 144px)'
                            : 'auto',
                        maxHeight: fillAvailableHeight ? '100%' : undefined,
                        minHeight: 0,
                        overflow: aiWorkspaceEnabled ? 'hidden' : 'visible',
                    }}
                >
                    <Paper
                        ref={assessmentAnchorRef}
                        data-testid="assessment-scroll-region"
                        variant={assessmentLayoutOption ? undefined : 'outlined'}
                        elevation={0}
                        sx={{
                            display: 'block',
                            minWidth: 0,
                            minHeight: 0,
                            height: aiWorkspaceEnabled ? '100%' : 'auto',
                            overflowY: aiWorkspaceEnabled ? 'auto' : 'visible',
                            bgcolor: 'background.paper',
                        }}
                    >
                        <BusySpinner busy={busy}>
                            <Grid container>
                        <ConditionalDisplay ifTrue={!assessmentLayoutOption}>
                            <Grid item xs={12}>
                                <Box sx={{ display: 'flex', alignItems: 'flex-start', flexWrap: { xs: 'wrap', sm: 'nowrap' }, gap: 1 }}>
                                    <Box flex={1} padding='1em'>
                                        <Stack direction='row' spacing={2} flexWrap="wrap">
                                            {assessment?.ScoreCard.ProficiencyGroupScores.map((pgs) =>
                                                <Stack direction='column' key={pgs.ProficiencyGroup} flex={1}>
                                                    <Typography color='secondary' align='center' fontSize={14} flexGrow={1}>{pgs.ProficiencyGroup}:</Typography>
                                                    <Typography align='center' fontSize={14} color='primary'>{pgs.Score}</Typography>
                                                </Stack>
                                            )}
                                            <Stack direction='column' flex={1}>
                                                <Typography align='center' fontSize={16} flexGrow={1}>Overall Score: </Typography>
                                                <Typography align='center' fontSize={16} color='primary'>{assessment?.ScoreCard.OverallScore}</Typography>
                                            </Stack>

                                        </Stack>
                                    </Box>
                                    <div>
                                        <Stack direction='row' spacing={1}>
                                            <Button variant="outlined" startIcon={<AutoAwesomeRounded />} onClick={() => setAiDialogOpen(true)}>
                                                Open Synergist AI
                                            </Button>
                                            <OngoingAssessmentPublishPopup
                                                talentId={talentId}
                                                hoverContent={I18N.Translate('Publish Ongoing Assessment')}
                                                icon={<PublishIcon htmlColor='green' fontSize='small' />}
                                                onClose={refreshAssessment}
                                                onPublished={handleAssessmentPublished}
                                                onError={(m, s, v) => handlePublishError(m, s, v)}
                                            />
                                        </Stack>
                                    </div>
                                </Box>
                            </Grid>
                        </ConditionalDisplay>
                        <Grid item xs={12}>
                            <Grid container spacing={2}>
                                <Grid item xs={12} md={3}>
                                    <ConditionalDisplay ifTrue={!busy}>
                                        <PanelSegment titleContent={<Typography>Proficiency Groups</Typography>}>
                                            <Box
                                                component="nav"
                                                aria-label="Proficiency groups"
                                                data-testid="proficiency-group-navigation"
                                                data-responsive-layout="horizontal-scroll"
                                                sx={{
                                                    display: { xs: 'flex', md: 'block' },
                                                    gap: { xs: 0.5, md: 0 },
                                                    overflowX: { xs: 'auto', md: 'visible' },
                                                    whiteSpace: { xs: 'nowrap', md: 'normal' },
                                                    py: { xs: 0.5, md: 1 },
                                                }}
                                            >
                                                {proficiencyGroups.map((pg) =>
                                                    <Tooltip key={pg.Id} title={pg.Name} placement="right" enterTouchDelay={0}>
                                                        <Button
                                                            type="button"
                                                            onClick={() => setSelectedProficiencyGroup(pg)}
                                                            aria-label={pg.Name}
                                                            aria-current={pg.Id === selectedProficiencyGroup?.Id ? 'page' : undefined}
                                                            variant={pg.Id === selectedProficiencyGroup?.Id ? 'contained' : 'text'}
                                                            sx={{
                                                                display: 'flex',
                                                                width: { xs: 'auto', md: '100%' },
                                                                minWidth: 0,
                                                                flex: { xs: '0 0 auto', md: 'initial' },
                                                                borderRadius: { xs: 1, md: 0 },
                                                                justifyContent: 'flex-start',
                                                                textAlign: 'left',
                                                                textTransform: 'none',
                                                                whiteSpace: 'normal',
                                                                overflowWrap: 'anywhere',
                                                            }}
                                                        >
                                                            <Stack direction="row" sx={{ minWidth: 0, flex: 1, flexDirection: 'row', gap: 0.5, justifyContent: 'space-between', alignItems: 'center' }}>
                                                                <Typography sx={{ minWidth: 0, overflowWrap: 'anywhere' }}>{pg.Name}</Typography>
                                                                <ConditionalDisplay ifTrue={assessmentLayoutOption}>
                                                                    <Typography fontSize="small">{assessment?.ScoreCard.ProficiencyGroupScores.find(f => f.ProficiencyGroup === pg.Name)?.Score}</Typography>
                                                                </ConditionalDisplay>
                                                            </Stack>
                                                        </Button>
                                                    </Tooltip>
                                                )}
                                            </Box>
                                        </PanelSegment>
                                    </ConditionalDisplay>
                                </Grid>
                                <Grid item xs={12} md={9} sx={{ minWidth: 0 }}>
                                    <ConditionalDisplay ifTrue={IsNotEmpty(selectedProficiencyGroup)}>
                                        {() =>
                                            <PanelSegment titleContent={
                                                <Stack direction="row" sx={{ flex: 1, flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center' }}>
                                                    <Typography>{selectedProficiencyGroup.Name}</Typography>
                                                    <Button variant="contained" color="primary" startIcon={<PrintIcon />} onClick={() => printDocument(assessment)}>
                                                        Download Report PDF
                                                    </Button>
                                                </Stack>
                                            }>
                                                {useTouchAssessmentCards ? (
                                                    <Stack
                                                        component="section"
                                                        aria-label={`${selectedProficiencyGroup.Name} proficiencies`}
                                                        spacing={1.5}
                                                        sx={{ p: { xs: 1.5, sm: 2 } }}
                                                    >
                                                        {assessment.Proficiencies
                                                            .filter(f => f.ProficiencyGroupId === selectedProficiencyGroup.Id)
                                                            .map((ap) => (
                                                                <MobileEntityCard
                                                                    key={ap.ProficiencyId}
                                                                    ariaLabel={`Proficiency: ${ap.Name}`}
                                                                    title={ap.Name}
                                                                    subtitle={ap.Type}
                                                                    fields={[{
                                                                        label: 'Selection',
                                                                        value: assessmentLayoutOption ? (
                                                                            ap?.Selection?.SelectionText ?? 'Not selected'
                                                                        ) : (
                                                                            <Select
                                                                                size="small"
                                                                                fullWidth
                                                                                value={ap?.Selection?.SelectionText ?? ''}
                                                                                inputProps={{ 'aria-label': `Selection for ${ap.Name}` }}
                                                                                onChange={(event) => handleSelectionChanged(ap, event.target.value)}
                                                                                sx={{ minHeight: 44 }}
                                                                            >
                                                                                {(ap.Type === 'Ability' ? abilityLevel : behaviorLevel)
                                                                                    .map((level) => <MenuItem key={level} value={level}>{level}</MenuItem>)}
                                                                            </Select>
                                                                        ),
                                                                        fullWidth: true,
                                                                    }]}
                                                                    actions={!assessmentLayoutOption ? (
                                                                        <TextEditorPopup
                                                                            hoverContent={`Add note for ${ap.Name}`}
                                                                            title={`Add Note: ${ap.Name}`}
                                                                            text={ap.Notes}
                                                                            textUpdated={(text) => handleNoteTextChanged(ap, text)}
                                                                        />
                                                                    ) : undefined}
                                                                />
                                                            ))}
                                                    </Stack>
                                                ) : (
                                                <div style={{ height: height - heightDetractor, width: '100%' }}>
                                                    <DataGridPro
                                                        density="compact"
                                                        hideFooter={!showFooter}
                                                        rows={assessment.Proficiencies.filter(f => f.ProficiencyGroupId === selectedProficiencyGroup.Id).map(x => { return { ...x, id: x.ProficiencyId } })}
                                                        columns={[
                                                            {
                                                                field: 'Type',
                                                                headerName: 'Type',
                                                                width: 150
                                                            },
                                                            {
                                                                field: 'Name',
                                                                headerName: 'Proficiency Name',
                                                                flex: 1
                                                            },
                                                            {
                                                                field: 'Selection',
                                                                headerName: 'Selection',
                                                                width: 200,
                                                                renderCell: ({ row: ap }) =>
                                                                    <>
                                                                        {IfTrue(ap.Type === 'Ability', () =>
                                                                            <>
                                                                                <ConditionalDisplay ifTrue={!assessmentLayoutOption}>
                                                                                    <Select
                                                                                        size="small"
                                                                                        fullWidth={true}
                                                                                        value={ap?.Selection?.SelectionText ?? ''}
                                                                                        onChange={(e) => handleSelectionChanged(ap, e.target.value)}>
                                                                                        {abilityLevel.map((level) => <MenuItem key={level} value={level}>{level}</MenuItem>)}
                                                                                    </Select>
                                                                                </ConditionalDisplay>
                                                                                <ConditionalDisplay ifTrue={assessmentLayoutOption}>
                                                                                    <TextField aria-readonly={true} value={ap?.Selection.SelectionText} />
                                                                                </ConditionalDisplay>
                                                                            </>
                                                                        )}
                                                                        {IfTrue(ap.Type === 'Behavior', () =>
                                                                            <>
                                                                                <ConditionalDisplay ifTrue={!assessmentLayoutOption}>
                                                                                    <Select
                                                                                        readOnly={assessmentLayoutOption}
                                                                                        size="small"
                                                                                        fullWidth={true}
                                                                                        value={ap?.Selection?.SelectionText ?? ''}
                                                                                        onChange={(e) => handleSelectionChanged(ap, e.target.value)}>
                                                                                        {behaviorLevel.map((level) => <MenuItem key={level} value={level}>{level}</MenuItem>)}
                                                                                    </Select>
                                                                                </ConditionalDisplay><ConditionalDisplay ifTrue={assessmentLayoutOption}>
                                                                                    <TextField aria-readonly={true} value={ap?.Selection.SelectionText} />
                                                                                </ConditionalDisplay>
                                                                            </>
                                                                        )}
                                                                    </>
                                                            },
                                                            {
                                                                field: 'Notes',
                                                                headerName: '',
                                                                sortable: false,
                                                                filterable: false,
                                                                disableColumnMenu: true,
                                                                width: 50,
                                                                renderCell: ({ row: ap }) => <TextEditorPopup hoverContent='Add Note' title={`Add Note: ${ap.Name}`} text={ap.Notes} textUpdated={(text) => handleNoteTextChanged(ap, text)} />
                                                            },
                                                        ]}
                                                        components={{
                                                            Toolbar: GridToolbar
                                                        }}
                                                    />
                                                </div>
                                                )}
                                            </PanelSegment>
                                        }
                                    </ConditionalDisplay>
                                </Grid>
                            </Grid>
                        </Grid>
                            </Grid>
                        </BusySpinner>
                    </Paper>
                    {aiWorkspaceEnabled && (
                        <ContextualAiDialog open={aiDialogOpen} onClose={() => setAiDialogOpen(false)} anchorEl={assessmentAnchorRef.current} title="Synergist AI — Ongoing Assessment" workflow="ongoing-assessment">
                            <AiReviewPanel
                                key={`${talentId}-${aiWorkspaceRevision}`}
                                talentId={talentId}
                                onDraftChanged={() => refreshAssessment(false)}
                                fillAvailableHeight
                                viewportMode={viewportMode}
                            />
                        </ContextualAiDialog>
                    )}
                </Box>
            </>
        );
}

export default OngoingAssessmentComponent;
